import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

type Firmware = {
  _id: string;
  originalName: string;
  version?: string | null;
  notes?: string;
  uploadedAt?: string;
  type: '2G' | '4G';
  downloadLink?: string;
  firmwareId?: number;
};

// Define types for handling actions
interface FirmwareTableProps {
  items: Firmware[];
  type: '2G' | '4G';
  editingId: string | null;
  editFormData: { version: string; notes: string; downloadLink: string };
  submitting: boolean;
  onEditChange: (field: string, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDownload: (firmware: Firmware) => void;
  onStartEdit: (firmware: Firmware) => void;
  onDelete: (id: string, name: string) => void;
  onReorder: (type: '2G' | '4G', orderedIds: string[]) => void;
}

const SortableRow: React.FC<{
  fw: Firmware;
  idx: number;
  editingId: string | null;
  editFormData: { version: string; notes: string; downloadLink: string };
  submitting: boolean;
  onEditChange: (field: string, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDownload: (firmware: Firmware) => void;
  onStartEdit: (firmware: Firmware) => void;
  onDelete: (id: string, name: string) => void;
}> = ({
  fw,
  idx,
  editingId,
  editFormData,
  submitting,
  onEditChange,
  onSave,
  onCancel,
  onDownload,
  onStartEdit,
  onDelete
}) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id: fw._id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: isDragging ? '#e0f2fe' : (idx % 2 === 0 ? '#ffffff' : '#fafbfc'),
      zIndex: isDragging ? 10 : 1,
      opacity: isDragging ? 0.8 : 1
    };

    return (
      <tr
        ref={setNodeRef}
        style={style}
        onMouseEnter={(e) => !isDragging && (e.currentTarget.style.backgroundColor = '#f0f9ff')}
        onMouseLeave={(e) => !isDragging && (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#fafbfc')}
      >
        <td style={{ padding: '12px', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', color: '#9ca3af' }}>
            <DragIndicatorIcon fontSize="small" />
          </div>
          <span style={{ fontWeight: 500 }}>{fw.firmwareId ?? (idx + 1)}</span>
        </td>
        <td style={{ padding: '12px', color: '#1f2937' }}>
          <span style={{ fontWeight: 500 }}>{fw.originalName}</span>
        </td>
        <td style={{ padding: '12px' }}>
          {editingId === fw._id ? (
            <input
              type="text"
              value={editFormData.version}
              onChange={(e) => onEditChange('version', e.target.value)}
              autoFocus
              style={{
                padding: '6px 8px',
                width: '100%',
                border: '1px solid #3b82f6',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            />
          ) : (
            <span style={{ color: fw.version ? '#1f2937' : '#9ca3af' }}>
              {fw.version || '-'}
            </span>
          )}
        </td>
        <td style={{ padding: '12px' }}>
          {editingId === fw._id ? (
            <input
              type="text"
              value={editFormData.notes}
              onChange={(e) => onEditChange('notes', e.target.value)}
              style={{
                padding: '6px 8px',
                width: '100%',
                border: '1px solid #3b82f6',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            />
          ) : (
            <span style={{ color: fw.notes ? '#1f2937' : '#9ca3af', fontSize: '0.875rem' }}>
              {fw.notes || '-'}
            </span>
          )}
        </td>
        <td style={{ padding: '12px', maxWidth: '150px' }}>
          {editingId === fw._id ? (
            <input
              type="text"
              value={editFormData.downloadLink}
              onChange={(e) => onEditChange('downloadLink', e.target.value)}
              placeholder="https://..."
              style={{
                padding: '6px 8px',
                width: '100%',
                border: '1px solid #3b82f6',
                borderRadius: '4px',
                fontSize: '0.75rem'
              }}
            />
          ) : (
            fw.downloadLink ? (
              <a
                href={fw.downloadLink}
                target="_blank"
                rel="noopener noreferrer"
                title={fw.downloadLink}
                style={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  wordBreak: 'break-all',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                View Link
              </a>
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>-</span>
            )
          )}
        </td>
        <td style={{ padding: '12px', textAlign: 'center' }}>
          {editingId === fw._id ? (
            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
              <button
                onClick={onSave}
                disabled={submitting}
                style={{
                  padding: '6px 14px',
                  backgroundColor: submitting ? '#d1d5db' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'opacity 0.2s'
                }}
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={onCancel}
                disabled={submitting}
                style={{
                  padding: '6px 14px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => onDownload(fw)}
                disabled={submitting}
                title="Download firmware from external link"
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  opacity: submitting ? 0.6 : 1,
                  transition: 'opacity 0.2s'
                }}
              >
                Download
              </button>
              <button
                onClick={() => onStartEdit(fw)}
                disabled={submitting}
                title="Edit firmware details"
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  opacity: submitting ? 0.6 : 1,
                  transition: 'opacity 0.2s'
                }}
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(fw._id, fw.originalName)}
                disabled={submitting}
                title="Delete this firmware"
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  opacity: submitting ? 0.6 : 1,
                  transition: 'opacity 0.2s'
                }}
              >
                Delete
              </button>
            </div>
          )}
        </td>
      </tr>
    );
  };

const FirmwareTable: React.FC<FirmwareTableProps> = ({
  items,
  type,
  editingId,
  editFormData,
  submitting,
  onEditChange,
  onSave,
  onCancel,
  onDownload,
  onStartEdit,
  onDelete,
  onReorder
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item._id === active.id);
      const newIndex = items.findIndex((item) => item._id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(type, newItems.map(i => i._id));
    }
  };

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
        {type} Firmware {items.length > 0 && <span style={{ color: '#6b7280', fontWeight: 400 }}>({items.length})</span>}
      </h3>
      <div style={{ overflowX: 'auto', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                <th style={{ padding: '12px', fontWeight: 600, textAlign: 'left', color: '#374151', width: '80px' }}>Sr. No.</th>
                <th style={{ padding: '12px', fontWeight: 600, textAlign: 'left', color: '#374151' }}>Filename</th>
                <th style={{ padding: '12px', fontWeight: 600, textAlign: 'left', color: '#374151' }}>Version</th>
                <th style={{ padding: '12px', fontWeight: 600, textAlign: 'left', color: '#374151' }}>Notes</th>
                <th style={{ padding: '12px', fontWeight: 600, textAlign: 'left', color: '#374151', maxWidth: '150px' }}>Link</th>
                <th style={{ padding: '12px', fontWeight: 600, textAlign: 'center', color: '#374151', minWidth: '320px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '32px 12px', textAlign: 'center', color: '#9ca3af' }}>
                    <div style={{ fontSize: '0.95rem' }}>No {type} firmware available</div>
                  </td>
                </tr>
              ) : (
                <SortableContext
                  items={items.map(i => i._id)}
                  strategy={verticalListSortingStrategy}
                >
                  {items.map((fw, idx) => (
                    <SortableRow
                      key={fw._id}
                      fw={fw}
                      idx={idx}
                      editingId={editingId}
                      editFormData={editFormData}
                      submitting={submitting}
                      onEditChange={onEditChange}
                      onSave={onSave}
                      onCancel={onCancel}
                      onDownload={onDownload}
                      onStartEdit={onStartEdit}
                      onDelete={onDelete}
                    />
                  ))}
                </SortableContext>
              )}
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
};

const FirmwareRepo: React.FC = () => {
  const [firmware2G, setFirmware2G] = useState<Firmware[]>([]);
  const [firmware4G, setFirmware4G] = useState<Firmware[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState({ version: '', notes: '', downloadLink: '' });
  const [uploadType, setUploadType] = useState<'2G' | '4G'>('2G');
  const [uploadFormData, setUploadFormData] = useState({ originalName: '', version: '', notes: '', downloadLink: '' });

  // Fetch firmware data
  const fetchFirmware = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get('/api/firmware');
      const data = response.data || [];
      const fw2G = data.filter((f: any) => f.type === '2G');
      const fw4G = data.filter((f: any) => f.type === '4G');
      setFirmware2G(fw2G);
      setFirmware4G(fw4G);
    } catch (error: any) {
      console.error('Error fetching firmware:', error);
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    fetchFirmware();
  }, []);

  // Handle add new firmware
  const handleAddFirmware = async () => {
    if (!uploadFormData.originalName.trim()) {
      alert('Please enter a filename');
      return;
    }
    if (!uploadFormData.downloadLink.trim()) {
      alert('Please enter a download link');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/firmware', {
        originalName: uploadFormData.originalName,
        version: uploadFormData.version,
        notes: uploadFormData.notes,
        downloadLink: uploadFormData.downloadLink,
        type: uploadType
      });
      setUploadFormData({ originalName: '', version: '', notes: '', downloadLink: '' });
      await fetchFirmware(true);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to add firmware';
      alert(errorMsg);
    }
    setSubmitting(false);
  };

  // Handle download
  const handleDownload = (firmware: Firmware) => {
    if (!firmware.downloadLink) {
      alert('No download link available');
      return;
    }
    window.open(firmware.downloadLink, '_blank');
  };

  // Handle edit
  const startEdit = (firmware: Firmware) => {
    setEditingId(firmware._id);
    setEditFormData({
      version: firmware.version || '',
      notes: firmware.notes || '',
      downloadLink: firmware.downloadLink || ''
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSubmitting(true);
    try {
      await api.put(`/api/firmware/${editingId}`, {
        version: editFormData.version,
        notes: editFormData.notes,
        downloadLink: editFormData.downloadLink
      });
      setEditingId(null);
      await fetchFirmware(true);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to update firmware';
      alert(errorMsg);
    }
    setSubmitting(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFormData({ version: '', notes: '', downloadLink: '' });
  };

  // Handle delete
  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    setSubmitting(true);
    try {
      await api.delete(`/api/firmware/${id}`);
      await fetchFirmware(true);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to delete firmware';
      alert(errorMsg);
    }
    setSubmitting(false);
  };

  const handleEditChange = (field: string, value: string) => {
    setEditFormData({ ...editFormData, [field]: value });
  };

  const handleReorder = async (type: '2G' | '4G', orderedIds: string[]) => {
    // Optimistically update UI
    if (type === '2G') {
      const reordered = orderedIds.map((id, index) => {
        const item = firmware2G.find(f => f._id === id);
        return { ...item!, firmwareId: index + 1 };
      });
      setFirmware2G(reordered as Firmware[]);
    } else {
      const reordered = orderedIds.map((id, index) => {
        const item = firmware4G.find(f => f._id === id);
        return { ...item!, firmwareId: index + 1 };
      });
      setFirmware4G(reordered as Firmware[]);
    }

    try {
      await api.put('/api/firmware/reorder', { orderedIds });
    } catch (error: any) {
      console.error('Failed to save order:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save new order';
      alert(errorMsg);
      fetchFirmware(true); // Revert on failure
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>
          Firmware Repository
        </h2>
        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
          Manage and organize firmware files for 2G and 4G networks
        </p>
      </div>

      {/* Add New Firmware Form */}
      <div style={{
        backgroundColor: '#f0f9ff',
        padding: '1.5rem',
        borderRadius: '0.75rem',
        marginBottom: '2rem',
        border: '1px solid #bfdbfe',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
      }}>
        <h3 style={{ marginTop: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', marginBottom: '1rem' }}>
          Add New Firmware
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth > 1200 ? '1fr 1fr 1fr 1fr auto' : '1fr 1fr',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
              Type *
            </label>
            <select
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value as '2G' | '4G')}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem',
                backgroundColor: '#ffffff',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              <option value="2G">2G Firmware</option>
              <option value="4G">4G Firmware</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
              Filename *
            </label>
            <input
              type="text"
              placeholder="e.g., Firmware_v1.0"
              value={uploadFormData.originalName}
              onChange={(e) => setUploadFormData({ ...uploadFormData, originalName: e.target.value })}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
                opacity: submitting ? 0.6 : 1
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
              Version
            </label>
            <input
              type="text"
              placeholder="e.g., v1.0.0"
              value={uploadFormData.version}
              onChange={(e) => setUploadFormData({ ...uploadFormData, version: e.target.value })}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
                opacity: submitting ? 0.6 : 1
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
              Download Link *
            </label>
            <input
              type="text"
              placeholder="https://example.com/file.bin"
              value={uploadFormData.downloadLink}
              onChange={(e) => setUploadFormData({ ...uploadFormData, downloadLink: e.target.value })}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
                opacity: submitting ? 0.6 : 1
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
              Notes
            </label>
            <input
              type="text"
              placeholder="Add notes..."
              value={uploadFormData.notes}
              onChange={(e) => setUploadFormData({ ...uploadFormData, notes: e.target.value })}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
                opacity: submitting ? 0.6 : 1
              }}
            />
          </div>
          <button
            onClick={handleAddFirmware}
            disabled={submitting}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: submitting ? '#d1d5db' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              transition: 'background-color 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#047857')}
            onMouseLeave={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#059669')}
          >
            {submitting ? 'Adding...' : 'Add Firmware'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Loading firmware...</div>
        </div>
      )}

      {/* Firmware Tables */}
      {!loading && (
        <>
          <FirmwareTable
            items={firmware2G}
            type="2G"
            editingId={editingId}
            editFormData={editFormData}
            submitting={submitting}
            onEditChange={handleEditChange}
            onSave={saveEdit}
            onCancel={cancelEdit}
            onDownload={handleDownload}
            onStartEdit={startEdit}
            onDelete={handleDelete}
            onReorder={handleReorder}
          />
          <FirmwareTable
            items={firmware4G}
            type="4G"
            editingId={editingId}
            editFormData={editFormData}
            submitting={submitting}
            onEditChange={handleEditChange}
            onSave={saveEdit}
            onCancel={cancelEdit}
            onDownload={handleDownload}
            onStartEdit={startEdit}
            onDelete={handleDelete}
            onReorder={handleReorder}
          />
        </>
      )}
    </div>
  );
};

export default FirmwareRepo;




